import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ProjectMilestone, ProjectOutcome, ServiceAssignment } from '../../graphql/types';
import {
  buildServiceNameMapFromResolution,
  buildServiceTeamIdMap,
  type ServiceResolutionMap,
  type TeamOption,
} from '../../lib/serviceResolution';
import { usePermission } from '../../hooks/usePermission';
import {
  updateServiceAssignment,
  deleteServiceAssignment,
  ApiError,
  type AssignmentStatusInput,
  type UpdateServiceAssignmentInput,
} from '../../lib/apiClient';
import { EmptyState, Pagination, DeleteConfirmationDialog } from '../shared';
import {
  resolveAssignedServiceName as resolveServiceName,
  filterAssignments,
  buildMilestoneNameMap,
  buildOutcomeNameMap,
  type AssignedServicesFilterState,
} from '../../lib/assignedServicesFilter';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './AssignedServicesTable.css';

export interface AssignedServicesTableProps {
  assignments: ServiceAssignment[];
  milestones: ProjectMilestone[];
  outcomes: ProjectOutcome[];
  /** @deprecated Pass `serviceResolution` instead — it resolves names for all
   * assigned services, not just owned ones. Kept for backward compatibility;
   * ignored when `serviceResolution` is provided. */
  serviceNames?: Record<string, string>;
  /**
   * Shared service->{name, teamId, teamName} resolution map (issue #495).
   * Bulk-resolves names/teams for every assignment regardless of ownership.
   */
  serviceResolution?: ServiceResolutionMap;
  /** Distinct team options for the team filter dropdown. */
  teamOptions?: TeamOption[];
  pageSizeOptions?: number[];
  /**
   * Called after a row's assignment is successfully edited or removed
   * (issue #655) so the parent can refetch `useProjectAssignments`. Omitting
   * it is safe — the table still updates its own edit/remove UI state, the
   * parent just won't see the change until its own refetch/poll fires.
   */
  onAssignmentsChanged?: () => void;
  /**
   * Called whenever the Team/Milestone/free-text filter values change
   * (issue #712), so the parent (Project Detail) can apply the identical
   * filter when building the Export CSV — Export must match exactly what
   * this table currently shows, not the full unfiltered assignment set.
   * The table remains otherwise self-contained/uncontrolled; omitting this
   * prop is safe and preserves the table's standalone behavior.
   */
  onFiltersChange?: (filters: AssignedServicesFilterState) => void;
}

type SortColumn = 'service' | 'milestone' | 'outcome' | 'status';
type SortDir = 'asc' | 'desc';

const DEFAULT_PAGE_SIZE = 10;

function statusLabel(status: string): string {
  switch (status) {
    case 'planned':
      return 'Planned';
    case 'in_progress':
      return 'In Progress';
    case 'done':
      return 'Done';
    default:
      return status;
  }
}

/** Row key shared by the table row, its inline edit row, and every data-testid. */
function rowKeyFor(a: Pick<ServiceAssignment, 'serviceId' | 'milestoneId'>): string {
  return `${a.serviceId}-${a.milestoneId}`;
}

const ForgeButton = 'forge-button' as React.ElementType;

export function AssignedServicesTable({
  assignments,
  milestones,
  outcomes,
  serviceNames,
  serviceResolution,
  teamOptions = [],
  pageSizeOptions = [10, 25, 50, 100],
  onAssignmentsChanged,
  onFiltersChange,
}: AssignedServicesTableProps): JSX.Element {
  const [sortCol, setSortCol] = useState<SortColumn>('milestone');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filter, setFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [milestoneFilter, setMilestoneFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Edit outcome/status/note in place (issue #655) — mirrors the Service
  // Detail "Project Assignments" edit flow (#535). Milestone is immutable
  // post-create (the PUT route keys on it), so only outcome/status/note are
  // editable here.
  const canManage = usePermission('service:update');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    outcomeId: string;
    assignmentStatus: AssignmentStatusInput;
    statusNote: string;
  }>({ outcomeId: '', assignmentStatus: 'planned', statusNote: '' });
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Remove an assignment (issue #655), gated behind the same DeleteConfirmationDialog
  // pattern used elsewhere in the app.
  const [removingAssignment, setRemovingAssignment] = useState<ServiceAssignment | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleOpenEdit = (a: ServiceAssignment): void => {
    setEditingKey(rowKeyFor(a));
    setEditForm({
      outcomeId: a.outcomeId,
      assignmentStatus: a.assignmentStatus as AssignmentStatusInput,
      statusNote: a.statusNote ?? '',
    });
    setEditError(null);
  };

  const handleCancelEdit = (): void => {
    setEditingKey(null);
    setEditError(null);
  };

  const handleSaveEdit = async (a: ServiceAssignment): Promise<void> => {
    setIsSavingEdit(true);
    setEditError(null);
    const updates: UpdateServiceAssignmentInput = {
      outcomeId: editForm.outcomeId,
      assignmentStatus: editForm.assignmentStatus,
      statusNote: editForm.statusNote || undefined,
    };
    try {
      await updateServiceAssignment(a.serviceId, a.milestoneId, updates);
      setEditingKey(null);
      onAssignmentsChanged?.();
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Failed to update assignment');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRequestRemove = (a: ServiceAssignment): void => {
    setRemovingAssignment(a);
    setRemoveError(null);
  };

  const handleCancelRemove = (): void => {
    setRemovingAssignment(null);
  };

  const handleConfirmRemove = async (): Promise<void> => {
    if (!removingAssignment) return;
    setIsRemoving(true);
    try {
      await deleteServiceAssignment(removingAssignment.serviceId, removingAssignment.milestoneId);
      setRemovingAssignment(null);
      onAssignmentsChanged?.();
    } catch (err) {
      setRemoveError(err instanceof ApiError ? err.message : 'Failed to remove assignment');
      setRemovingAssignment(null);
    } finally {
      setIsRemoving(false);
    }
  };

  // `serviceResolution` (issue #495) resolves names/teams for every assigned
  // service, not just owned ones. `serviceNames` is kept only for backward
  // compatibility with callers that have not migrated yet.
  const serviceNameMap = useMemo(() => {
    if (serviceResolution) {
      return buildServiceNameMapFromResolution(serviceResolution);
    }
    return new Map(Object.entries(serviceNames ?? {}));
  }, [serviceResolution, serviceNames]);

  const serviceTeamIdMap = useMemo(() => {
    if (!serviceResolution) return new Map<string, string | null>();
    return buildServiceTeamIdMap(serviceResolution);
  }, [serviceResolution]);

  const milestoneMap = useMemo(() => buildMilestoneNameMap(milestones), [milestones]);
  const outcomeMap = useMemo(() => buildOutcomeNameMap(outcomes), [outcomes]);

  // Milestone filter options (issue #711): distinct milestones present in the
  // *assignment set* (not the full project milestone catalog), mirroring how
  // teamOptions is scoped to teams that actually own an assigned service.
  const milestoneFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { milestoneId: string; milestoneName: string }[] = [];
    for (const a of assignments) {
      if (seen.has(a.milestoneId)) continue;
      seen.add(a.milestoneId);
      opts.push({
        milestoneId: a.milestoneId,
        milestoneName: milestoneMap.get(a.milestoneId) ?? a.milestoneId,
      });
    }
    return opts.sort((a, b) => a.milestoneName.localeCompare(b.milestoneName));
  }, [assignments, milestoneMap]);

  const handleSort = (col: SortColumn): void => {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const handleFilterChange = (value: string): void => {
    setFilter(value);
    setPage(1);
  };

  const handleTeamFilterChange = (value: string): void => {
    setTeamFilter(value);
    setPage(1);
  };

  const handleMilestoneFilterChange = (value: string): void => {
    setMilestoneFilter(value);
    setPage(1);
  };

  // Report the current filter values upward (issue #712) so the parent can
  // apply the identical filter when building the Export CSV. Depends only on
  // the three filter primitives (not the computed `filtered` array), so it
  // fires exactly once per user filter interaction — never loops even if the
  // parent's own re-render produces new `assignments`/`onFiltersChange`
  // references.
  useEffect(() => {
    onFiltersChange?.({ textFilter: filter, teamFilter, milestoneFilter });
  }, [filter, teamFilter, milestoneFilter, onFiltersChange]);

  const filtered = useMemo(
    () =>
      filterAssignments(
        assignments,
        { textFilter: filter, teamFilter, milestoneFilter },
        {
          serviceTeamIdMap,
          serviceNameMap,
          milestoneNameMap: milestoneMap,
          outcomeNameMap: outcomeMap,
        }
      ),
    [
      assignments,
      filter,
      teamFilter,
      milestoneFilter,
      serviceTeamIdMap,
      serviceNameMap,
      milestoneMap,
      outcomeMap,
    ]
  );

  const sorted = [...filtered].sort((a, b) => {
    let aVal = '';
    let bVal = '';
    switch (sortCol) {
      case 'service':
        aVal = resolveServiceName(a.serviceId, serviceNameMap);
        bVal = resolveServiceName(b.serviceId, serviceNameMap);
        break;
      case 'milestone':
        aVal = milestoneMap.get(a.milestoneId) ?? '';
        bVal = milestoneMap.get(b.milestoneId) ?? '';
        break;
      case 'outcome':
        aVal = outcomeMap.get(a.outcomeId) ?? '';
        bVal = outcomeMap.get(b.outcomeId) ?? '';
        break;
      case 'status':
        aVal = a.assignmentStatus;
        bVal = b.assignmentStatus;
        break;
    }
    const cmp = aVal.localeCompare(bVal);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalCount = sorted.length;
  const pageStart = (page - 1) * pageSize;
  const paged = sorted.slice(pageStart, pageStart + pageSize);

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(1);
  };

  if (assignments.length === 0) {
    return (
      <EmptyState
        icon="assignment"
        title="No services assigned"
        description="Use 'Assign Service' to assign owned services to milestones and outcomes."
      />
    );
  }

  const colHeader = (col: SortColumn, label: string): JSX.Element => (
    <th
      className="assigned-services-th"
      onClick={() => handleSort(col)}
      aria-sort={sortCol === col ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      role="columnheader"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleSort(col);
      }}
    >
      {label}
      {sortCol === col && (
        <span aria-hidden="true" className="assigned-services-sort-icon">
          {sortDir === 'asc' ? ' ▲' : ' ▼'}
        </span>
      )}
    </th>
  );

  return (
    <div className="assigned-services-table-wrapper">
      <div className="assigned-services-filters-row">
        <input
          className="search-input assigned-services-filter"
          value={filter}
          onChange={(e) => handleFilterChange(e.target.value)}
          placeholder="Filter assignments…"
          aria-label="Filter assignments"
        />
        {teamOptions.length > 0 && (
          <select
            className="filter-select"
            value={teamFilter}
            onChange={(e) => handleTeamFilterChange(e.target.value)}
            aria-label="Filter by team"
          >
            <option value="">All Teams</option>
            {teamOptions.map((team) => (
              <option key={team.teamId} value={team.teamId}>
                {team.teamName}
              </option>
            ))}
          </select>
        )}
        {milestoneFilterOptions.length > 0 && (
          <select
            className="filter-select"
            value={milestoneFilter}
            onChange={(e) => handleMilestoneFilterChange(e.target.value)}
            aria-label="Filter by milestone"
          >
            <option value="">All Milestones</option>
            {milestoneFilterOptions.map((m) => (
              <option key={m.milestoneId} value={m.milestoneId}>
                {m.milestoneName}
              </option>
            ))}
          </select>
        )}
      </div>
      <table className="assigned-services-table" aria-label="Assigned services">
        <thead>
          <tr>
            {colHeader('service', 'Service')}
            {colHeader('milestone', 'Milestone')}
            {colHeader('outcome', 'Outcome')}
            {colHeader('status', 'Status')}
            <th className="assigned-services-th">Note</th>
            {canManage && <th className="assigned-services-th">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {paged.map((a) => {
            const rowKey = rowKeyFor(a);
            const isEditingRow = editingKey === rowKey;
            const milestoneName = milestoneMap.get(a.milestoneId) ?? a.milestoneId;
            return (
              <Fragment key={rowKey}>
                <tr data-testid={`assignment-row-${rowKey}`}>
                  <td className="forge-typography--body2">
                    {resolveServiceName(a.serviceId, serviceNameMap)}
                  </td>
                  <td className="forge-typography--body2">{milestoneName}</td>
                  <td className="forge-typography--body2">
                    {outcomeMap.get(a.outcomeId) ?? a.outcomeId}
                  </td>
                  <td className="forge-typography--body2">{statusLabel(a.assignmentStatus)}</td>
                  <td className="forge-typography--caption assigned-services-note">
                    {a.statusNote ?? '—'}
                  </td>
                  {canManage && (
                    <td>
                      <div className="assigned-services-actions">
                        {!isEditingRow && (
                          <>
                            <ForgeButton
                              type="button"
                              variant="text"
                              onClick={() => handleOpenEdit(a)}
                              aria-label={`Edit assignment for ${milestoneName}`}
                              data-testid={`edit-assignment-${rowKey}`}
                            >
                              <forge-icon name="edit" slot="start" aria-hidden="true"></forge-icon>
                              Edit
                            </ForgeButton>
                            <ForgeButton
                              type="button"
                              variant="text"
                              onClick={() => handleRequestRemove(a)}
                              aria-label={`Remove assignment for ${milestoneName}`}
                              data-testid={`remove-assignment-${rowKey}`}
                            >
                              <forge-icon name="close" slot="start" aria-hidden="true"></forge-icon>
                              Remove
                            </ForgeButton>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                {isEditingRow && (
                  <tr
                    className="assigned-services-edit-row"
                    data-testid={`edit-assignment-form-${rowKey}`}
                  >
                    <td colSpan={canManage ? 6 : 5}>
                      {editError && (
                        <p
                          className="forge-typography--caption assigned-services-edit-error"
                          role="alert"
                          data-testid="edit-assignment-error"
                        >
                          {editError}
                        </p>
                      )}
                      <div className="assigned-services-edit-fields">
                        <div className="assigned-services-edit-field">
                          <label
                            className="forge-typography--caption"
                            htmlFor={`edit-outcome-${rowKey}`}
                          >
                            Outcome
                          </label>
                          <select
                            id={`edit-outcome-${rowKey}`}
                            className="filter-select"
                            value={editForm.outcomeId}
                            onChange={(e) =>
                              setEditForm((p) => ({ ...p, outcomeId: e.target.value }))
                            }
                            aria-label="Edit outcome"
                          >
                            <option value="">— Select outcome —</option>
                            {outcomes.map((oc) => (
                              <option key={oc.entityId} value={oc.entityId}>
                                {oc.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="assigned-services-edit-field">
                          <label
                            className="forge-typography--caption"
                            htmlFor={`edit-status-${rowKey}`}
                          >
                            Status
                          </label>
                          <select
                            id={`edit-status-${rowKey}`}
                            className="filter-select"
                            value={editForm.assignmentStatus}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                assignmentStatus: e.target.value as AssignmentStatusInput,
                              }))
                            }
                            aria-label="Edit status"
                          >
                            <option value="planned">Planned</option>
                            <option value="in-progress">In Progress</option>
                            <option value="done">Done</option>
                          </select>
                        </div>
                        <div className="assigned-services-edit-field">
                          <label
                            className="forge-typography--caption"
                            htmlFor={`edit-note-${rowKey}`}
                          >
                            Status note
                          </label>
                          <input
                            id={`edit-note-${rowKey}`}
                            className="search-input"
                            value={editForm.statusNote}
                            onChange={(e) =>
                              setEditForm((p) => ({ ...p, statusNote: e.target.value }))
                            }
                            aria-label="Edit status note"
                          />
                        </div>
                        <div className="assigned-services-edit-actions">
                          <ForgeButton
                            type="button"
                            variant="raised"
                            disabled={isSavingEdit || !editForm.outcomeId}
                            onClick={() => void handleSaveEdit(a)}
                            data-testid={`save-assignment-${rowKey}`}
                          >
                            {isSavingEdit ? 'Saving…' : 'Save'}
                          </ForgeButton>
                          <ForgeButton
                            type="button"
                            variant="outlined"
                            onClick={handleCancelEdit}
                            data-testid={`cancel-edit-assignment-${rowKey}`}
                          >
                            Cancel
                          </ForgeButton>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {removeError && (
        <p className="forge-typography--caption assigned-services-edit-error" role="alert">
          {removeError}
        </p>
      )}
      {removingAssignment !== null && (
        <DeleteConfirmationDialog
          open
          title="Remove Assignment"
          message="Are you sure you want to remove this service from the project?"
          itemName={`${resolveServiceName(removingAssignment.serviceId, serviceNameMap)} / ${
            milestoneMap.get(removingAssignment.milestoneId) ?? removingAssignment.milestoneId
          }`}
          onConfirm={() => void handleConfirmRemove()}
          onCancel={handleCancelRemove}
          isDeleting={isRemoving}
        />
      )}
      {totalCount === 0 && assignments.length > 0 && (
        <p className="forge-typography--body2 assigned-services-no-match">
          No assignments match the filter.
        </p>
      )}
      <Pagination
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
    </div>
  );
}
